import { Button, Icons, Input, Label, SecretInput, Spinner } from '@repo/ui'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { toast } from 'sonner'

import { AuthShell } from '@/components/auth/auth-shell'
import { useAuth } from '@/hooks/use-auth'
import { authApi } from '@/lib/api'
import { signInWithGoogle } from '@/lib/social-auth'

export const Route = createFileRoute('/cadastrar')({
  component: SignupPage,
})

function SignupPage() {
  const navigate = useNavigate()
  const { refresh } = useAuth()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  async function handleGoogleSignup() {
    setGoogleLoading(true)
    try {
      await signInWithGoogle(`${window.location.origin}/inbox`)
    } catch {
      toast.error('Não foi possível iniciar o cadastro com Google.')
      setGoogleLoading(false)
    }
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()

    if (password.length < 8) {
      toast.error('A senha deve ter no mínimo 8 caracteres')
      return
    }

    setSubmitting(true)

    try {
      await authApi.post('/api/auth/sign-up/email', { email, password, name })
      await refresh()
      toast.success('Conta criada! Bem-vindo!')
      await navigate({ to: '/sem-organizacao' })
    } catch {
      setSubmitting(false)
      return
    }

    setSubmitting(false)
  }

  return (
    <AuthShell
      title="Criar sua conta"
      subtitle="Comece a usar a AgroSDR em segundos"
    >
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={googleLoading || submitting}
        onClick={() => void handleGoogleSignup()}
      >
        {googleLoading ? <Spinner /> : <Icons.google />}
        Cadastrar com Google
      </Button>

      <div className="my-6 flex items-center gap-3">
        <div className="bg-border h-px flex-1" />
        <span className="text-muted-foreground text-xs">ou</span>
        <div className="bg-border h-px flex-1" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nome completo</Label>
          <Input
            id="name"
            type="text"
            required
            autoComplete="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Senha</Label>
          <SecretInput
            id="password"
            visibilityLabel="senha"
            required
            autoComplete="new-password"
            placeholder="Mínimo 8 caracteres"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? <Spinner /> : null}
          Criar conta
        </Button>
      </form>
      <p className="text-muted-foreground mt-6 text-center text-sm">
        Ja tem conta?{' '}
        <Link
          to="/entrar"
          search={{ returnTo: undefined }}
          className="text-primary font-medium hover:underline"
        >
          Entrar
        </Link>
      </p>
    </AuthShell>
  )
}
